import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useMediaSrc } from '../../library/useMediaSrc'
import { OF, type OfApi } from '../ofApi'
import { groupEvents, toEvent, toPromotions, upcomingQuery, type ScheduledEvent, type Promotion } from './homeData'

const TYPE_ICON: Record<ScheduledEvent['type'], string> = { post: 'file-text', chat: 'message-square', stream: 'radio' }

/** Home's right column: Subscription, Scheduled events (→ Queue) and OnlyFans' promotion banner. */
export function HomeAside({ of, subscribePrice, onOpenPage }: { of: OfApi; subscribePrice: number | null; onOpenPage: (page: string) => void }) {
  const { t, i18n } = useTranslation('ofHome')
  const [events, setEvents] = useState<ScheduledEvent[] | null>(null)
  const [promos, setPromos] = useState<Promotion[]>([])

  useEffect(() => {
    let live = true
    of.get<{ list?: Record<string, unknown>[] }>('schedules', upcomingQuery(), OF)
      .then((p) => live && setEvents((p?.list ?? []).map(toEvent)))
      .catch(() => live && setEvents([]))
    of.get('users/promotions', {}, OF)
      .then((p) => live && setPromos(toPromotions(p)))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [of])

  const dayLabel = (day: string) => {
    const d = new Date(`${day}T00:00`)
    return {
      date: d.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
      weekday: d.toLocaleDateString(i18n.language, { weekday: 'short' }),
    }
  }

  return (
    <aside className="ofh-aside" aria-label={t('aside.label')}>
      <section className="ofh-card">
        <h3 className="ofh-card-title">{t('aside.subscription')}</h3>
        <div className="ofh-sub">
          <span>{t('aside.subscriptionPrice')}</span>
          <span className="ofp-muted">
            {subscribePrice === null ? '' : subscribePrice > 0 ? t('aside.perMonth', { price: subscribePrice.toFixed(2) }) : t('aside.free')}
          </span>
        </div>
      </section>

      <section className="ofh-card" aria-busy={events === null}>
        <h3 className="ofh-card-title">{t('aside.scheduled')}</h3>
        {events !== null && events.length === 0 && <p className="ofp-muted ofh-card-empty">{t('aside.noScheduled')}</p>}
        {events !== null &&
          groupEvents(events).map((g) => {
            const label = dayLabel(g.day)
            return (
              <div key={g.day} className="ofh-ev-day">
                <span className="ofh-ev-date">
                  <span>{label.date}</span>
                  <span className="ofp-muted">{label.weekday}</span>
                </span>
                <ul className="ofh-ev-list">
                  {g.items.map((e) => (
                    <li key={e.id} className="ofh-ev">
                      <span className="ofh-ev-text">
                        <span className="ofh-ev-time">
                          {new Date(e.at).toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' })}
                          <FeatherIcon name={TYPE_ICON[e.type]} size={13} aria-label={t(`aside.type.${e.type}`)} />
                        </span>
                        <span className="ofp-muted">
                          <FeatherIcon name="image" size={11} /> {e.mediaCount}
                        </span>
                      </span>
                      {e.thumb && <EventThumb url={e.thumb} />}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        <button type="button" className="ofh-card-link" onClick={() => onOpenPage('queue')}>
          {t('aside.viewQueue')}
        </button>
      </section>

      {promos.map((p) => (
        <Promo key={p.image} promo={p} onOpenPage={onOpenPage} />
      ))}
    </aside>
  )
}

function EventThumb({ url }: { url: string }) {
  const { ref, src } = useMediaSrc(url)
  return <span ref={ref as React.RefObject<HTMLSpanElement>} className="ofh-ev-thumb">{src && <img src={src} alt="" />}</span>
}

/** OnlyFans' banner; its /my/chats link opens the Account's Chats page. */
function Promo({ promo, onOpenPage }: { promo: Promotion; onOpenPage: (page: string) => void }) {
  const { t } = useTranslation('ofHome')
  const { ref, src } = useMediaSrc(promo.image)
  const page = /\/my\/chats\b/.test(promo.url) ? 'chats' : null
  const img = <span ref={ref as React.RefObject<HTMLSpanElement>} className="ofh-promo-img">{src && <img src={src} alt={t('aside.promotion')} />}</span>
  return page ? (
    <button type="button" className="ofh-card ofh-promo" onClick={() => onOpenPage(page)}>
      {img}
    </button>
  ) : (
    <div className="ofh-card ofh-promo">{img}</div>
  )
}
